import { CaretDownIcon, DotsThreeIcon } from '@phosphor-icons/react'
import React, { MouseEvent } from 'react'

import { Button } from '../button'
import { Checkbox } from '../checkbox'
import { DropdownMenu, DropdownMenuItem } from '../dropdown-menu'
import { Icon } from '../icon'
import { ROW_ACTIONS_DROPDOWN_ZINDEX } from './table.constants'
import * as S from './table.styled'
import { getColumnId, RowAction, TableColumn } from './table.types'
import { TableCell } from './table-cell'

export interface TableRowProps<T> {
  item: T
  rowKey: string | number
  columns: TableColumn<T>[]
  // Note: by column id - see Table's columnWidths.
  columnWidths: Record<string, string>
  pinnedColumnId: string | null
  isBulkMode: boolean
  isSelected: boolean
  showRowActions: boolean
  menuItems: DropdownMenuItem[]
  onSelectRow: (rowKey: string | number, checked: boolean) => void
  rowAction?: RowAction<T>
  hasCollapsableRow?: boolean
  isExpanded?: boolean
  onToggleExpand?: () => void
}

export const TableRow = <T,>({
  item,
  rowKey,
  columns,
  columnWidths,
  pinnedColumnId,
  isBulkMode,
  isSelected,
  showRowActions,
  menuItems,
  rowAction,
  onSelectRow,
  hasCollapsableRow,
  isExpanded,
  onToggleExpand,
}: TableRowProps<T>) => {
  const handleRowClick = () => {
    if (hasCollapsableRow) {
      onToggleExpand?.()
      return
    }

    rowAction?.onClick(item)
  }

  const handleToggleExpand = (event: MouseEvent) => {
    // Note: Prevent triggering primary row action when toggling collapse
    event.stopPropagation()
    if (!hasCollapsableRow) return
    onToggleExpand?.()
  }

  return (
    <S.BodyRow
      $clickable={!!rowAction || !!hasCollapsableRow}
      $expanded={isExpanded}
      onClick={handleRowClick}
    >
      {isBulkMode && (
        <S.CheckboxBodyCell onClick={(e) => e.stopPropagation()}>
          <S.CheckboxWrapper>
            <Checkbox
              checked={isSelected}
              // Note: per the table design, the selection column holds the 20px (medium) checkbox.
              size="medium"
              onChange={() => onSelectRow(rowKey, !isSelected)}
            />
          </S.CheckboxWrapper>
        </S.CheckboxBodyCell>
      )}
      {columns.map((column, columnIndex) => {
        const columnId = getColumnId(column)
        const isPinned = pinnedColumnId === columnId
        const width = columnWidths[columnId]
        const isLastColumn = columnIndex === columns.length - 1

        if (isPinned) {
          return (
            <S.PinnedBodyCell
              key={columnId}
              $width={width}
              $isBulkMode={isBulkMode}
              data-pinned="true"
              data-highlighted={column.highlighted || undefined}
            >
              <TableCell breakLines={column.breakLines}>{column.render(item)}</TableCell>
              {isLastColumn && hasCollapsableRow && (
                <S.CollapsableToggleWrapper onClick={handleToggleExpand}>
                  <Button onlyBox use="ghost" size="small">
                    <S.CaretIcon icon={CaretDownIcon} $expanded={!!isExpanded} />
                  </Button>
                </S.CollapsableToggleWrapper>
              )}
            </S.PinnedBodyCell>
          )
        }

        return (
          <S.BodyCell key={columnId} data-highlighted={column.highlighted || undefined}>
            <TableCell breakLines={column.breakLines}>{column.render(item)}</TableCell>
            {isLastColumn && hasCollapsableRow && (
              <S.CollapsableToggleWrapper onClick={handleToggleExpand}>
                <Button onlyBox use="ghost" size="small">
                  <S.CaretIcon icon={CaretDownIcon} $expanded={!!isExpanded} />
                </Button>
              </S.CollapsableToggleWrapper>
            )}
          </S.BodyCell>
        )
      })}
      {showRowActions && (
        <S.RowActionsCell onClick={(e) => e.stopPropagation()}>
          <S.RowActionsWrapper>
            <DropdownMenu
              items={menuItems}
              dropdownZIndex={ROW_ACTIONS_DROPDOWN_ZINDEX}
              trigger={({ isOpen }) => (
                <S.RowActionMenuTrigger onlyBox use="primary-light" size="small" $isOpen={isOpen}>
                  <Icon icon={DotsThreeIcon} weight="bold" />
                </S.RowActionMenuTrigger>
              )}
            />
          </S.RowActionsWrapper>
        </S.RowActionsCell>
      )}
    </S.BodyRow>
  )
}
